import { useTranslation } from '@thoughtspot/i18n';
import { SegmentControl } from '@thoughtspot/radiant-react/widgets/segment-control';
import { SegmentedControlItem } from '@thoughtspot/radiant-react/widgets/segment-control/segment-control-item';
import { isSpreadsheetsEnabled } from '@thoughtspot/session-service';
import React, { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { trackSpreadsheetToggleClick } from '../../components/answer-spreadsheet/spreadsheet-mixpanel-util';
import styles from './search-data-view-mode-toggle.module.scss';

// Radiant's `classNames` props replace its own class names rather than adding
// to them, which is the point: the shared segment-control names are matched by
// unscoped Playwright selectors, and this control renders in the top nav on
// every answer page. `classNames` is not forwarded from the control to its
// items (base-control.tsx clones children with only index/isSelected/onSelect),
// so each item is given its own.
const CONTROL_CLASS_NAMES = {
    segmentedControl: styles.viewSwitch,
    selectedHighlight: styles.viewSwitchHighlight,
};

const ITEM_CLASS_NAMES = {
    segmentedItem: styles.viewSwitchItem,
    selected: styles.viewSwitchItemSelected,
};

/**
 * Search Data / Spreadsheet view switch, hosted by the top nav on the answer
 * and saved-answer routes.
 *
 * The routes declare it as their `topNavStartSlot`; this component decides
 * whether it may actually be shown. The flag and embed gates live here, at
 * render time, because route maps are module-level literals evaluated at
 * import, before the preauth handshake that populates session info.
 */
export const SearchDataViewModeToggle: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { navService, metricsService } = useAppContext();

    const isVisible = isSpreadsheetsEnabled() && !isEmbeddedApp();

    const isSpreadsheet = location.pathname.endsWith('/spreadsheet');
    const selectedIndex = isSpreadsheet ? 1 : 0;

    // SegmentControl fires onSelect more than once per user action: the item's
    // click handler fires it, then flipping the item's internal isSelected
    // state fires it again, and the initially selected item fires it on mount.
    // Only a change away from the last index we handled is a real toggle.
    const lastHandledIndexRef = useRef(selectedIndex);

    // A route change we did not cause -- back/forward, a deep link, a
    // programmatic redirect -- also moves the selection and makes radiant
    // re-fire onSelect. Absorbing it has to happen during render, not in an
    // effect: radiant's onSelect is fired from a child effect, and child
    // effects run before the parent's.
    lastHandledIndexRef.current = selectedIndex;

    const handleViewSelect = useCallback(
        (index: number) => {
            if (index === lastHandledIndexRef.current) {
                return;
            }
            lastHandledIndexRef.current = index;
            if (index === 1) {
                trackSpreadsheetToggleClick(metricsService);
                if (!isSpreadsheet) {
                    navService.goToPath(`${location.pathname}/spreadsheet`);
                }
            } else if (isSpreadsheet) {
                navService.goToPath(
                    location.pathname.replace(/\/spreadsheet$/, ''),
                );
            }
        },
        [navService, metricsService, location.pathname, isSpreadsheet],
    );

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles.viewModeToggle}>
            <SegmentControl
                selectedIndex={selectedIndex}
                onSelect={handleViewSelect}
                classNames={CONTROL_CLASS_NAMES}
            >
                <SegmentedControlItem
                    classNames={ITEM_CLASS_NAMES}
                    isSelected={!isSpreadsheet}
                >
                    <span className={styles.viewSwitchLabel}>
                        {t('spreadsheet.viewMode.query')}
                    </span>
                </SegmentedControlItem>
                <SegmentedControlItem
                    classNames={ITEM_CLASS_NAMES}
                    isSelected={isSpreadsheet}
                >
                    <span className={styles.viewSwitchLabel}>
                        {t('spreadsheet.viewMode.spreadsheet')}
                    </span>
                </SegmentedControlItem>
            </SegmentControl>
        </div>
    );
};
